import { useEffect, useState } from 'react';
import { Button } from 'reactstrap';

type Theme = 'light' | 'dark';
type Density = 'compact' | 'cozy' | 'comfortable';

const KEY_THEME = 'vp.tweak.theme';
const KEY_DENSITY = 'vp.tweak.density';

function readSaved<T extends string>(key: string, fallback: T): T {
  return (localStorage.getItem(key) as T) || fallback;
}

export function TweaksPanel() {
  const [theme, setTheme] = useState<Theme>(() => readSaved(KEY_THEME, 'light'));
  const [density, setDensity] = useState<Density>(() => readSaved(KEY_DENSITY, 'cozy'));

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(KEY_THEME, theme);
  }, [theme]);

  useEffect(() => {
    document.documentElement.dataset.density = density;
    localStorage.setItem(KEY_DENSITY, density);
  }, [density]);

  if (import.meta.env.VITE_DEV_TWEAKS !== 'true') return null;

  return (
    <div
      style={{
        position: 'fixed', bottom: 12, right: 12, zIndex: 9999,
        background: 'var(--vp-surface)', border: '1px solid var(--vp-border)',
        borderRadius: 'var(--vp-r-md)', padding: 10, fontSize: 12,
        boxShadow: 'var(--vp-shadow-md)', display: 'flex', gap: 12, alignItems: 'center',
      }}
      data-testid="tweaks-panel"
    >
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ color: 'var(--vp-text-3)' }}>Theme</span>
        {(['light', 'dark'] as const).map((t) => (
          <Button
            key={t}
            size="sm"
            color={theme === t ? 'primary' : 'light'}
            onClick={() => setTheme(t)}
          >{t}</Button>
        ))}
      </div>
      <span style={{ width: 1, height: 16, background: 'var(--vp-border)' }} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ color: 'var(--vp-text-3)' }}>Density</span>
        {(['compact', 'cozy', 'comfortable'] as const).map((d) => (
          <Button
            key={d}
            size="sm"
            color={density === d ? 'primary' : 'light'}
            onClick={() => setDensity(d)}
          >{d}</Button>
        ))}
      </div>
    </div>
  );
}
